import React, { ReactNode } from "react";
import { DailyTable } from "@/features/daily/components/DailyTable";
import { DailyTip } from "@/features/daily/components/DailyTip";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import { SeriesChart } from "@/features/daily/components/executive/SeriesChart";
import type { SeriesCurrencyVm, SeriesDayVm } from "@/features/daily/components/executive/series-view-model";
import { statTileViewModel } from "@/features/daily/components/stat-tile-view-model";

/** D129: the chart's tabular alternative, for screen readers only (WCAG 1.1.1). */
const SeriesTable: React.FC<{ days: SeriesDayVm[]; currency: string | null; caption: string }> = ({ days, currency, caption }) => (
    <div className="daily-visually-hidden">
        <DailyTable
            caption={caption}
            columns={[
                { key: "day", header: "Día", render: (day: SeriesDayVm) => `${day.label}${day.partial ? " · parcial" : ""}` },
                { key: "current", header: "Actual", numeric: true, render: (day: SeriesDayVm) => dailyFormat.money(day.current, currency) },
                // D139: per-type columns only when both types appear in the period.
                ...(days.some((day) => day.currentByType.order !== 0) && days.some((day) => day.currentByType.deposit !== 0) ? [
                    { key: "orders", header: "Órdenes", numeric: true, render: (day: SeriesDayVm) => dailyFormat.money(day.currentByType.order, currency) },
                    { key: "deposits", header: "Depósitos", numeric: true, render: (day: SeriesDayVm) => dailyFormat.money(day.currentByType.deposit, currency) },
                ] : []),
                {
                    key: "reference",
                    header: "Mismo día, periodo anterior",
                    numeric: true,
                    render: (day: SeriesDayVm) => (day.reference === null ? dailyFormat.absent : dailyFormat.money(day.reference, currency)),
                },
                { key: "cumulative", header: "Acumulado", numeric: true, render: (day: SeriesDayVm) => dailyFormat.money(day.cumulativeCurrent, currency) },
                {
                    key: "cumulativeReference",
                    header: "Acumulado anterior",
                    numeric: true,
                    render: (day: SeriesDayVm) => (day.cumulativeReference === null ? dailyFormat.absent : dailyFormat.money(day.cumulativeReference, currency)),
                },
            ]}
            rows={days}
            rowKey={(day) => day.date}
            emptyMessage="No ha transcurrido ningún día de este periodo."
        />
    </div>
);

type TrendProps = {
    title: string;
    measureLabel: string;
    tone: "blue" | "green";
    vm: SeriesCurrencyVm;
    currencyValue: string | null;
    /** D129: the currency select lives in a chart card's header. */
    control?: ReactNode;
    /** D143: the page's source and evidence notes, in the title's (i). */
    notes: string[];
};

/**
 * D110 + B1: one measure per card, day by day, against the same days of the previous
 * period (dashed). Only elapsed days are drawn — no projection, no future day at zero
 * — and the variation says "—" when there is nothing to compare with.
 */
export const TrendCard: React.FC<TrendProps> = ({ title, measureLabel, tone, vm, currencyValue, control, notes }) => (
    <section className="daily-panel daily-trend" data-tone={tone} aria-label={title}>
        <div className="daily-panel__head">
            <div className="daily-trend__title">
                <h2>{title}</h2>
                <DailyTip
                    id={`daily-trend-tip-${measureLabel}`}
                    label={`Cómo leer ${title}`}
                    lines={["Sólo días transcurridos: sin proyección ni días futuros a cero.", `${vm.windowLabel} · ${vm.currencyLabel}`, ...notes]}
                />
            </div>
            <div className="daily-legend">
                <span><b className="daily-key" aria-hidden="true" />Actual</span>
                <span><b className="daily-key daily-key--previous" aria-hidden="true" />Anterior</span>
                {control}
            </div>
        </div>
        <SeriesChart
            days={vm.days}
            tone={tone}
            currency={currencyValue}
            label={`${measureLabel} de cada día en ${vm.currencyLabel}: periodo actual frente al anterior. Los datos están en la tabla.`}
        />
        <SeriesTable days={vm.days} currency={currencyValue} caption={`${measureLabel} por día en ${vm.currencyLabel}`} />
        <dl className="daily-trend__stats">
            <div>
                <dt>Actual</dt>
                <dd>{vm.totalLabel}</dd>
            </div>
            <div>
                <dt>Anterior</dt>
                <dd>{vm.referenceTotalLabel}</dd>
            </div>
            <div>
                <dt className="daily-visually-hidden">Variación</dt>
                <dd className="daily-trend__variation" data-tone={vm.variationTone}>
                    <span aria-hidden="true">{statTileViewModel.arrow(vm.variationDirection)} </span>
                    {vm.variationLabel}
                </dd>
            </div>
        </dl>
    </section>
);
